import { pageTitle } from '@palaccount/config';
import type { DayStatus } from '@palaccount/contracts';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Ltr } from '@/components/amount';
import { Pager } from '@/components/pager';
import { ConfirmBox } from '@/components/confirm-box';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { usePagedState, usePageRange } from '@/lib/paging';
import { useCan } from '@/lib/permissions';
import { cn } from '@/lib/utils';

import {
  useAssignShift,
  useCheckIn,
  useCheckOut,
  useCorrections,
  useDecideCorrection,
  usePagedEmployees,
  useMyDay,
  useRegister,
  useAttendanceSummary,
  useShifts,
  useSubmitCorrection,
} from './api';
import { HrTabs } from './hr-tabs';

/**
 * HR-05 §5's shift-assignment list — a tick list, so 25 rows is a block a
 * supervisor can hold in their head before pressing Apply.
 */
const SHIFT_ASSIGN_PAGE_SIZE = 25;

/**
 * HR-03 §5d's grid is WIDE — thirty-one columns — so its page is shorter than
 * a table's: 20 rows is what fits beside the day headers without the reader
 * losing the row they are on.
 */
const REGISTER_PAGE_SIZE = 20;

/** HR-03 §5's Needs-attention queue — a decision list, one block at a time. */
const CORRECTIONS_PAGE_SIZE = 25;

/** The square letters + tones of HR-03 §5d — the legend covers all nine. */
const SQUARES: Record<DayStatus, { letter: string; tone: string }> = {
  present: { letter: 'P', tone: 'bg-green-500/10 text-green-700 dark:text-green-400' },
  absent: { letter: 'A', tone: 'bg-destructive/10 text-destructive' },
  late: { letter: 'L', tone: 'bg-amber-500/10 text-amber-700 dark:text-amber-400' },
  half_day: { letter: '½', tone: 'bg-orange-500/10 text-orange-700 dark:text-orange-400' },
  on_leave: { letter: 'LV', tone: 'bg-blue-500/10 text-blue-700 dark:text-blue-400' },
  holiday: { letter: 'H', tone: 'bg-muted text-muted-foreground' },
  weekly_off: { letter: '—', tone: 'bg-muted/50 text-muted-foreground' },
  // BR-HR-03-07: "Incomplete must be drawn distinctly from Absent." Both are
  // destructive-toned because both are problems, so the tone alone would not
  // separate them — the distinction is carried by the LETTER ('!' against
  // 'A') and by the outline-versus-fill treatment. Changing either back to
  // match `absent` above breaks this rule, not merely the styling.
  incomplete: { letter: '!', tone: 'border border-destructive text-destructive bg-destructive/5' },
  future: { letter: '', tone: 'bg-transparent' },
};

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * HR-03 — Attendance: the self-service card on top (rung 4 — no role
 * needed), then Register / Monthly summary / Needs attention / Shifts.
 * Clicking a square opens the correction block: reason REQUIRED, submit
 * creates a PENDING request — the one correction path (BR-HR-03-10). There
 * is no direct-write control anywhere on this screen, for anybody.
 */
export function AttendancePage() {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === 'ar';
  const canView = useCan('hr.view');
  const [month, setMonth] = useState(currentMonth());
  const [tab, setTab] = useState<'register' | 'summary' | 'attention' | 'shifts'>('register');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const myDay = useMyDay();
  const checkIn = useCheckIn();
  const checkOut = useCheckOut();
  /*
   * NFR-42 — the month was always a WINDOW (PAGINATION.md §5d); the rows are
   * a page now too. One row per employee, each carrying thirty-one derived
   * squares, so a company of 400 drew 12,400 of them to fill one screen.
   * Both grids share a named page, because they are two views of one list and
   * a reader who is on page 2 of the register expects page 2 of the summary.
   */
  const gridPaging = usePagedState(REGISTER_PAGE_SIZE, 'staff');
  const register = useRegister(month, canView, gridPaging.page, REGISTER_PAGE_SIZE);
  const summary = useAttendanceSummary(
    month,
    canView && tab === 'summary',
    gridPaging.page,
    REGISTER_PAGE_SIZE,
  );
  const gridRange = usePageRange(
    tab === 'summary' ? summary.data : register.data,
    REGISTER_PAGE_SIZE,
  );
  /*
   * NFR-42 — the queue is a page, and the PENDING BADGE is a count from the
   * server rather than a `.filter().length` over it. The badge used to be
   * folded from the whole list; a page would have made it "pending on this
   * page" (PAGINATION.md §3 step 10), which is the one number on this screen
   * a supervisor acts on.
   */
  const correctionPaging = usePagedState(CORRECTIONS_PAGE_SIZE, 'corrections');
  const corrections = useCorrections({}, correctionPaging.page, CORRECTIONS_PAGE_SIZE);
  const correctionRange = usePageRange(corrections.data, CORRECTIONS_PAGE_SIZE);
  const pending = useCorrections({ status: 'pending' }, 1, 1);
  const decide = useDecideCorrection();

  const [correcting, setCorrecting] = useState<{ employeeId: string; day: string } | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('hr.attendanceTitle'));
  }, [t]);

  // The SERVER's count of pending corrections — a one-row read whose `total`
  // is the badge, not the length of whatever page is on screen.
  const pendingCount = pending.data?.total ?? 0;

  return (
    <div className="grid gap-4">
      <HrTabs />

      {/* The self-service card — every user, role or no role (§9). */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-4">
          <div className="me-auto">
            <h2 className="text-sm font-semibold">{t('hr.myAttendance')}</h2>
            {myDay.data?.employee ? (
              <p className="text-sm text-muted-foreground">
                {ar ? myDay.data.employee.fullNameAr : myDay.data.employee.fullName}
                {myDay.data.shift
                  ? ` · ${myDay.data.shift.name} (${myDay.data.shift.startsAt}–${myDay.data.shift.endsAt})`
                  : ''}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">{t('hr.noEmployeeRecord')}</p>
            )}
          </div>
          {myDay.data?.today ? (
            <p className="text-sm">
              {t('hr.checkedIn')}: <Ltr value={myDay.data.today.checkIn ?? '—'} className="font-mono" /> ·{' '}
              {t('hr.checkedOut')}: <Ltr value={myDay.data.today.checkOut ?? '—'} className="font-mono" />
            </p>
          ) : null}
          {myDay.data?.employee ? (
            myDay.data.today?.checkIn == null ? (
              <Button
                onClick={() =>
                  checkIn.mutate(undefined, {
                    onError: (cause) => setError(cause instanceof Error ? cause.message : null),
                  })
                }
              >
                {t('hr.checkIn')}
              </Button>
            ) : myDay.data.today.checkOut == null ? (
              <Button
                variant="destructive"
                onClick={() =>
                  checkOut.mutate(undefined, {
                    onError: (cause) => setError(cause instanceof Error ? cause.message : null),
                  })
                }
              >
                {t('hr.checkOut')}
              </Button>
            ) : null
          ) : null}
        </CardContent>
      </Card>

      {error ? (
        <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}
      {notice ? <p className="rounded-md bg-primary/10 p-3 text-sm">{notice}</p> : null}

      {canView ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {(['register', 'summary', 'attention', 'shifts'] as const).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm',
                  tab === key ? 'bg-accent font-medium' : 'text-muted-foreground hover:bg-accent/50',
                )}
              >
                {t(`hr.attTab.${key}`)}
                {key === 'attention' && pendingCount > 0 ? (
                  <span className="ms-1 rounded-full bg-amber-500/10 px-1.5 text-xs">{pendingCount}</span>
                ) : null}
              </button>
            ))}
            <Input
              type="month"
              value={month}
              onChange={(e) => gridPaging.change(setMonth)(e.target.value)}
              className="ms-auto h-9 w-44"
              aria-label={t('hr.month')}
            />
          </div>

          {tab === 'register' ? (
            <Card>
              <CardContent className="grid gap-3 overflow-x-auto">
                <p className="text-xs text-muted-foreground">{t('hr.registerHint')}</p>
                {register.data ? (
                  <table className="text-xs">
                    <thead>
                      <tr>
                        <th className="p-1 text-start">{t('hr.employee')}</th>
                        {Array.from({ length: register.data.daysInMonth }, (_, i) => (
                          <th key={i} className="p-0.5 font-mono font-normal text-muted-foreground">
                            {i + 1}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {register.data.rows.map((row) => (
                        <tr key={row.employeeId}>
                          <td className="whitespace-nowrap p-1 pe-3 text-sm">
                            {ar ? row.fullNameAr : row.fullName}
                          </td>
                          {row.days.map((status, i) => {
                            const square = SQUARES[status];
                            const day = `${month}-${String(i + 1).padStart(2, '0')}`;
                            return (
                              <td key={i} className="p-0.5">
                                <button
                                  type="button"
                                  aria-label={`${row.fullName} ${day}: ${t(`hr.day.${status}`)}`}
                                  title={t(`hr.day.${status}`)}
                                  onClick={() => setCorrecting({ employeeId: row.employeeId, day })}
                                  className={cn(
                                    'flex size-6 items-center justify-center rounded text-[10px] font-bold',
                                    square.tone,
                                  )}
                                >
                                  {square.letter}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy />
                )}
                {(register.data?.total ?? 0) > 0 ? (
                  <Pager
                    page={gridPaging.page}
                    pages={gridRange.pages}
                    first={gridRange.first}
                    last={gridRange.last}
                    total={gridRange.total}
                    onPage={gridPaging.setPage}
                    label={t('hr.attTab.register')}
                  />
                ) : null}
                {/* The legend covers every status the grid draws (BR-HR-03-17),
                    and it does so BY CONSTRUCTION: it iterates the same
                    SQUARES map the grid renders from, so a status cannot be
                    drawn without appearing here.

                    ERR-HR-03-05 is the prototype defect that construction
                    closes — "weekly off and future are drawn in the grid but
                    missing from the legend". Both are keys of SQUARES, so
                    both are listed. Replacing this iteration with a hand-written
                    list would reintroduce the defect silently. */}
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                  {(Object.keys(SQUARES) as DayStatus[]).map((status) => (
                    <span key={status} className="flex items-center gap-1">
                      <span
                        className={cn(
                          'flex size-4 items-center justify-center rounded text-[9px] font-bold',
                          SQUARES[status].tone,
                        )}
                      >
                        {SQUARES[status].letter}
                      </span>
                      {t(`hr.day.${status}`)}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : null}

          {tab === 'summary' && summary.data ? (
            <Card>
              <CardContent className="overflow-x-auto">
                <p className="mb-2 text-xs text-muted-foreground">{t('hr.summaryHint')}</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('hr.employee')}</TableHead>
                      <TableHead>{t('hr.shift')}</TableHead>
                      <TableHead className="text-end">{t('hr.day.present')}</TableHead>
                      <TableHead className="text-end">{t('hr.day.absent')}</TableHead>
                      <TableHead className="text-end">{t('hr.day.late')}</TableHead>
                      {/* ERR-HR-03-04: the prototype counted half-days and
                          incomplete days "and then never showed them" in any
                          column. These two heads and their two cells below are
                          the columns the defects list asks for. */}
                      <TableHead className="text-end">{t('hr.day.half_day')}</TableHead>
                      <TableHead className="text-end">{t('hr.day.incomplete')}</TableHead>
                      <TableHead className="text-end">{t('hr.day.on_leave')}</TableHead>
                      <TableHead className="text-end">{t('hr.overtime')}</TableHead>
                      <TableHead className="text-end">{t('hr.hours')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.data.rows.map((row) => (
                      <TableRow key={row.employeeId}>
                        <TableCell>{ar ? row.fullNameAr : row.fullName}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{row.shiftName ?? '—'}</TableCell>
                        <TableCell className="text-end font-mono">{row.present}</TableCell>
                        <TableCell className="text-end font-mono">{row.absent}</TableCell>
                        <TableCell className="text-end font-mono">{row.late}</TableCell>
                        <TableCell className="text-end font-mono">{row.halfDays}</TableCell>
                        <TableCell className="text-end font-mono">{row.incomplete}</TableCell>
                        <TableCell className="text-end font-mono">{row.leave}</TableCell>
                        <TableCell className="text-end font-mono">{row.overtime}</TableCell>
                        <TableCell className="text-end font-mono">{row.hours}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {(summary.data?.total ?? 0) > 0 ? (
                  <Pager
                    page={gridPaging.page}
                    pages={gridRange.pages}
                    first={gridRange.first}
                    last={gridRange.last}
                    total={gridRange.total}
                    onPage={gridPaging.setPage}
                    label={t('hr.attTab.summary')}
                  />
                ) : null}
                <p className="mt-2 text-xs text-muted-foreground">{t('hr.summaryFeedsPayroll')}</p>
              </CardContent>
            </Card>
          ) : null}

          {tab === 'attention' ? (
            <Card>
              <CardContent className="grid gap-3">
                <h3 className="text-sm font-semibold">{t('hr.pendingCorrections')}</h3>
                {(corrections.data?.corrections ?? []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('hr.noCorrections')}</p>
                ) : (
                  (corrections.data?.corrections ?? []).map((correction) => (
                    <CorrectionRow
                      key={correction.id}
                      correction={correction}
                      onDecide={(approve, comment) => {
                        setError(null);
                        decide.mutate(
                          { correctionId: correction.id, approve, ...(comment ? { comment } : {}) },
                          {
                            onError: (cause) =>
                              setError(cause instanceof Error ? cause.message : t('common.saveFailed')),
                          },
                        );
                      }}
                    />
                  ))
                )}
                {(corrections.data?.total ?? 0) > 0 ? (
                  <Pager
                    page={correctionPaging.page}
                    pages={correctionRange.pages}
                    first={correctionRange.first}
                    last={correctionRange.last}
                    total={correctionRange.total}
                    onPage={correctionPaging.setPage}
                    label={t('hr.pendingCorrections')}
                  />
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          {tab === 'shifts' ? <ShiftsBlock /> : null}
        </>
      ) : null}

      {correcting ? (
        <CorrectionDialog
          employeeId={correcting.employeeId}
          day={correcting.day}
          onClose={() => setCorrecting(null)}
          onSubmitted={(message) => {
            setCorrecting(null);
            setNotice(message);
          }}
        />
      ) : null}
    </div>
  );
}

function CorrectionRow({
  correction,
  onDecide,
}: {
  correction: {
    id: string;
    employeeName: string;
    day: string;
    originalIn: string | null;
    originalOut: string | null;
    requestedIn: string | null;
    requestedOut: string | null;
    reason: string;
    status: string;
    decisionComment: string | null;
  };
  onDecide: (approve: boolean, comment?: string) => void;
}) {
  const { t } = useTranslation();
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState('');
  const [approving, setApproving] = useState(false);

  return (
    <div className="grid gap-2 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">{correction.employeeName}</span>
        <Ltr value={correction.day} className="font-mono text-muted-foreground" />
        <Ltr
          value={`${correction.originalIn ?? '—'} → ${correction.originalOut ?? '—'} ⇒ ${correction.requestedIn ?? '—'} → ${correction.requestedOut ?? '—'}`}
          className="font-mono text-xs"
        />
        <span
          className={cn(
            'ms-auto rounded-full px-2 py-0.5 text-[11px]',
            correction.status === 'pending'
              ? 'bg-muted'
              : correction.status === 'approved'
                ? 'bg-green-500/10 text-green-700 dark:text-green-400'
                : 'bg-destructive/10 text-destructive',
          )}
        >
          {t(`hr.correction.${correction.status}`)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">{correction.reason}</p>
      {correction.decisionComment ? (
        <p className="text-xs text-destructive">{correction.decisionComment}</p>
      ) : null}
      {/* Only a PENDING correction has a decision to make — a decided one has
          none for anybody. The permission greys the pair (BR-PERM-18). */}
      {correction.status === 'pending' ? (
        rejecting ? (
          <div className="grid gap-2">
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('hr.rejectCommentHint')}
              rows={2}
            />
            <div className="flex gap-2">
              {/* BR-PERM-16: really disabled until the comment is typed. */}
              <Button
                variant="destructive"
                size="sm"
                disabled={comment.trim() === ''}
                onClick={() => onDecide(false, comment)}
              >
                {t('hr.reject')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setRejecting(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <Gated permission="hr.approve" reasonKey="perm.cannot.decideCorrections">
              <Button size="sm" onClick={() => setApproving(true)}>
                {t('hr.approve')}
              </Button>
            </Gated>
            <Gated permission="hr.approve" reasonKey="perm.cannot.decideCorrections">
              <Button variant="outline" size="sm" onClick={() => setRejecting(true)}>
                {t('hr.reject')}
              </Button>
            </Gated>
            {/*
              16.5.3 names approving an attendance correction as one of the
              THREE places the shared confirmation box is used. This was
              `window.confirm`, which fails ADM-02 §8 twice over: it is browser
              chrome, so there is no padlock, no amber tile and no 420px panel
              the product controls; and its confirm button reads OK, where §8
              says "the verb, never the word OK".

              §8's split puts APPROVE here and REJECT in its own dialog above:
              approving needs no explanation, rejecting requires a typed
              comment (BR-PERM-16 keeps that button really disabled until it
              is typed). Two shapes, deliberately.
            */}
            <ConfirmBox
              open={approving}
              title={t('hr.approveTitle')}
              body={t('hr.approveConfirm')}
              confirmLabel={t('hr.approve')}
              onCancel={() => setApproving(false)}
              onConfirm={() => {
                setApproving(false);
                onDecide(true);
              }}
            />
          </div>
        )
      ) : null}
    </div>
  );
}

function CorrectionDialog({
  employeeId,
  day,
  onClose,
  onSubmitted,
}: {
  employeeId: string;
  day: string;
  onClose: () => void;
  onSubmitted: (message: string) => void;
}) {
  const { t } = useTranslation();
  const submit = useSubmitCorrection();
  const [requestedIn, setRequestedIn] = useState('');
  const [requestedOut, setRequestedOut] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const canSubmit = reason.trim() !== '' && (requestedIn !== '' || requestedOut !== '');

  return (
    <Card>
      <CardContent className="grid gap-3">
        <h3 className="text-sm font-semibold">
          {t('hr.correctDay')} · <Ltr value={day} className="font-mono" />
        </h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1">
            <Label htmlFor="corr-in">{t('hr.checkedIn')}</Label>
            <Input id="corr-in" type="time" value={requestedIn} onChange={(e) => setRequestedIn(e.target.value)} />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="corr-out">{t('hr.checkedOut')}</Label>
            <Input id="corr-out" type="time" value={requestedOut} onChange={(e) => setRequestedOut(e.target.value)} />
          </div>
        </div>
        <div className="grid gap-1">
          <Label htmlFor="corr-reason">{t('hr.correctionReason')}</Label>
          <Textarea id="corr-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
        </div>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <div className="flex gap-2">
          {/* BR-HR-03-10: disabled until a reason is typed — and enforced
              again by the server, which is the real gate. */}
          <Button
            disabled={!canSubmit}
            onClick={() =>
              submit.mutate(
                {
                  employeeId,
                  day,
                  ...(requestedIn !== '' ? { requestedIn } : {}),
                  ...(requestedOut !== '' ? { requestedOut } : {}),
                  reason,
                },
                {
                  onSuccess: (body) => onSubmitted(body.message),
                  onError: (cause) =>
                    setError(cause instanceof Error ? cause.message : t('common.saveFailed')),
                },
              )
            }
          >
            {t('hr.submitForApproval')}
          </Button>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ShiftsBlock() {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === 'ar';
  const { data: shifts } = useShifts();
  const assign = useAssignShift();
  const [ticked, setTicked] = useState<string[]>([]);
  const [shiftId, setShiftId] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  /*
   * NFR-42 — the assignment list is a LIST, not a picker: it ticks people
   * rather than resolving one, so it gets a page and a search box rather than
   * a typeahead. `?shiftPage=` is a NAMED page because HR-05 draws more than
   * one block (PAGINATION.md §3 step 12).
   *
   * `ticked` survives paging by construction — it holds ids, and a row is
   * checked by asking whether its id is in the set — so a supervisor can tick
   * people across several pages and apply once.
   */
  const paging = usePagedState(SHIFT_ASSIGN_PAGE_SIZE, 'shift');
  const [q, setQ] = useState('');
  const { data: page } = usePagedEmployees(
    { q: q.trim() || undefined },
    paging.page,
    paging.pageSize,
  );
  const employees = page?.employees;
  const range = usePageRange(page, paging.pageSize);

  return (
    <div className="grid gap-4">
      <Card>
        <CardContent>
          <h3 className="mb-2 text-sm font-semibold">{t('hr.shiftDefinitions')}</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('hr.shift')}</TableHead>
                <TableHead>{t('hr.shiftStart')}</TableHead>
                <TableHead>{t('hr.shiftEnd')}</TableHead>
                <TableHead>{t('hr.shiftBreak')}</TableHead>
                <TableHead>{t('hr.shiftGrace')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(shifts ?? []).map((shift) => (
                <TableRow key={shift.id}>
                  <TableCell>{ar ? shift.nameAr : shift.name}</TableCell>
                  <TableCell className="font-mono">{shift.startsAt}</TableCell>
                  <TableCell className="font-mono">{shift.endsAt}</TableCell>
                  <TableCell className="font-mono">{shift.breakMinutes} {t('hr.min')}</TableCell>
                  <TableCell className="font-mono">{shift.graceMinutes} {t('hr.min')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* The card itself is drawn whatever the role holds: a supervisor who
          cannot assign shifts should still learn that shifts are assignable,
          and the Apply button inside says who to ask (BR-PERM-18). */}
      <Card>
          <CardContent className="grid gap-3">
            <h3 className="text-sm font-semibold">{t('hr.shiftAssignment')}</h3>
            <p className="text-xs text-muted-foreground">{t('hr.shiftAssignHint')}</p>
            {notice ? <p className="rounded-md bg-primary/10 p-2 text-xs">{notice}</p> : null}
            {ticked.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2 rounded-md bg-accent p-2">
                <span className="text-sm">{t('hr.selectedCount', { count: ticked.length })}</span>
                <NativeSelect
                  value={shiftId}
                  onChange={(e) => setShiftId(e.target.value)}
                  className="h-9 w-56"
                  aria-label={t('hr.shift')}
                >
                  <option value="">—</option>
                  {(shifts ?? []).map((shift) => (
                    <option key={shift.id} value={shift.id}>
                      {ar ? shift.nameAr : shift.name}
                    </option>
                  ))}
                </NativeSelect>
                <Gated permission="hr.edit" reasonKey="perm.cannot.assignShifts">
                <Button
                  size="sm"
                  disabled={shiftId === ''}
                  onClick={() =>
                    assign.mutate(
                      { employeeIds: ticked, shiftId },
                      {
                        onSuccess: (body) => {
                          setTicked([]);
                          setNotice(t('hr.shiftAssigned', { count: body.assigned }));
                        },
                      },
                    )
                  }
                >
                  {t('hr.apply')}
                </Button>
                </Gated>
              </div>
            ) : null}
            <Input
              aria-label={t('hr.searchEmployees')}
              placeholder={t('hr.searchEmployees')}
              value={q}
              onChange={(event) => paging.change(setQ)(event.target.value)}
              className="h-9"
            />
            {(employees ?? []).map((employee) => (
              <label key={employee.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={ticked.includes(employee.id)}
                  onChange={(e) =>
                    setTicked((prev) =>
                      e.target.checked ? [...prev, employee.id] : prev.filter((id) => id !== employee.id),
                    )
                  }
                />
                <span className="min-w-0 flex-1 truncate">{ar ? employee.fullNameAr : employee.fullName}</span>
                <span className="text-xs text-muted-foreground">
                  {(shifts ?? []).find((s) => s.id === employee.shiftId)?.name ?? '—'}
                </span>
              </label>
            ))}
            {(page?.total ?? 0) > 0 ? (
              <Pager
                page={paging.page}
                pages={range.pages}
                first={range.first}
                last={range.last}
                total={range.total}
                onPage={paging.setPage}
                label={t('hr.shiftAssignment')}
              />
            ) : null}
          </CardContent>
      </Card>
    </div>
  );
}
